<template>
  <div>
    <canvas ref="chartElement">wer</canvas>
  </div>
</template>

<script setup>
  import { onMounted, watch, computed, ref } from 'vue'
  import Chart from 'chart.js'
  import 'chartjs-adapter-date-fns'
  import { de } from 'date-fns/locale'
  import { useDate } from '@/composables/useDate.ts'
  import { usePortfolioChartColors } from '@/components/efriends/portfolioChartColors.js'
  import { usePortfolioStore } from '@/stores/usePortfolioStore'
  import { useMapStore } from '@/stores/useMapStore'
  import { useUnits } from '@/composables/useUnits.ts'

  const portfolioStore = usePortfolioStore()
  const mapStore = useMapStore()
  const { getPowerColor, priceColor, clpColor } = usePortfolioChartColors()
  const { moment } = useDate()

  const chart = ref()
  const chartElement = ref(null)
  const { formatWattage } = useUnits()
  const formatPrice = (value) =>
    `${(value * 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
    })} ct`

  const chartSettings = {
    type: 'bar',
    options: {
      tooltips: {
        mode: 'index',
        callbacks: {
          title: (item) => {
            const time = moment(item[0].label)
            return time.format('HH:mm')
          },
          label: (item, data) => {
            const rawLabel = data.datasets[item.datasetIndex].label

            switch (rawLabel) {
              case 'Price':
                return `Preis/kWh: ${formatPrice(item.value)}`
              case 'CLP':
                return `Prognostizierter Verbrauch: ${formatWattage(item.value)}`
              default:
                return (
                  `${
                    mapStore.getCubeByName(rawLabel, 'tour').params.nickName
                  }: ${formatWattage(item.value)}` || rawLabel
                )
            }
          },
        },
      },
      legend: { display: false },
      scales: {
        xAxes: [
          {
            type: 'time',
            time: {
              displayFormats: {
                day: 'E dd.MM',
                hour: 'HH:mm',
              },
            },
            adapters: {
              date: {
                locale: de
              }
            },
            categorySpacing: 1,
            stacked: true,
            gridLines: {
              color: 'rgba(0, 0, 0, 0.03)',
              zeroLineColor: 'transparent',
            },
            display: true,
            ticks: {
              fontColor: '#888',
              autoSkip: true,
              autoSkipPadding: 40,
              maxRotation: 0,
              minRotation: 0,
              includeBounds: true,
            },
          },
        ],
        yAxes: [
          {
            id: 'wattage',
            stacked: true,
            gridLines: {
              color: 'rgba(0, 0, 0, 0.03)',
            },
            ticks: {
              fontColor: '#888',
              autoSkip: true,
              autoSkipPadding: 100,
              callback: (value) => formatWattage(Math.abs(value)),
            },
          },
          {
            id: 'price',
            stacked: false,
            position: 'right',
            gridLines: {
              color: 'rgba(0, 0, 0, 0.03)',
            },
            ticks: {
              suggestedMin: 0,
              suggestedMax: 0.1,
              fontColor: '#888',
              autoSkip: true,
              autoSkipPadding: 100,
              callback: (value) => formatPrice(value),
            },
          },
        ],
      },
    },
  }

  const parseChartData = ({ chartData, clpData, priceDatakWh }) => {
    let config = chartSettings

    if (!chartData || !clpData || !priceDatakWh) { return config }

    const TOP_PADDING = 1.2 // 20% Padding on top

    // Cap the wattage at the clp limit
    config.options.scales.yAxes[0].ticks.max = Math.max(
      ...clpData.map((entry) => Number(entry) * TOP_PADDING)
    )

    config.data = {
      labels: chartData.dateTimes,
      datasets: [],
    }
    for (let cube in chartData.values) {
      config.data.datasets.push({
        type: 'bar',
        label: cube,
        backgroundColor: getPowerColor(cube) + '89',
        data: chartData.values[cube],
        yAxisID: 'wattage',
        order: 5,
      })
    }
    config.data.datasets.push({
      type: 'line',
      label: 'CLP',
      backgroundColor: clpColor,
      data: clpData,
      yAxisID: 'wattage',
      stacked: false,
      pointStyle: 'circle',
      pointRadius: 0,
      borderColor: '#999',
      borderWidth: 1,
      borderJoinStyle: 'round',
      barPercentage: 0.8,
      categoryPercentage: 1,
      order: 5,
    })
    config.data.datasets.push({
      type: 'line',
      label: 'Price',
      data: priceDatakWh,
      yAxisID: 'price',
      fill: false,
      stacked: false,
      pointStyle: 'circle',
      pointRadius: 0,
      tension: 0.1,
      borderJoinStyle: 'round',
      borderWidth: 1.5,
      borderColor: priceColor,
      barPercentage: 0.8,
      categoryPercentage: 1,
      order: 1,
    })

    return config
  }

  const chartData = computed(() => portfolioStore.chartData)

  onMounted(() => {
    chart.value = new Chart(
      chartElement.value,
      parseChartData(chartData.value)
    )
  })

  watch(chartData, (newValue) => {
    if (chart.value) {
      chart.value.data = parseChartData(newValue).data
      chart.value.update()
    }
  })
</script>
